/**
 * Rendering money and token counts (spec 072 FR-016, FR-017).
 *
 * Amounts arrive as strings, not numbers — the service holds them in `NUMERIC` and
 * serialises them exactly, so parsing one into a JavaScript `number` is the one step
 * that could reintroduce the imprecision the backend went to trouble to avoid.
 *
 * Most functions here only format. `sumMoney` is the exception and stays within the
 * same rule: it adds with `BigInt` over scaled integers, so the arithmetic is exact and
 * no amount is ever a `number` (spec 089 FR-015, research R3).
 */

/**
 * Money for a reader: the currency, then the amount, truncated to cents.
 *
 * Truncation is deliberate. `FR-017` forbids a rounded figure implying an overrun that
 * has not happened, and `$9.999` displayed as `$10.00` against a `$10.00` limit reads as
 * exhausted when it is not. Dropping the sub-cent remainder can understate by less than
 * a cent and can never invent a breach.
 *
 * **The truncation happens on the string, never on a float.** `Math.floor(value * 100)`
 * is the obvious implementation and it is wrong at cent boundaries: `19.99 * 100` is
 * `1998.9999999999998` in IEEE-754, so flooring renders `$19.98` — an understatement of
 * a full cent, not the sub-cent remainder promised above. It misprices a large share of
 * ordinary amounts (0.29, 1.15, 2.03, 8.29, 19.99 …), and only some of them, which is
 * the worst way for a money bug to behave. The service serialises `NUMERIC` exactly for
 * precisely this reason; parsing that into a `number` and multiplying gives the
 * imprecision straight back.
 */
export function formatMoney(
  amount: string,
  currency = 'USD',
  { showBelowCent = false }: { showBelowCent?: boolean } = {},
): string {
  const cents = truncateToCents(amount);
  // `Number('')` and `Number('   ')` are both 0, so a numeric guard alone would render
  // a missing amount as a confident `$0.00` — the exact failure this feature exists to
  // prevent, since "spent nothing" and "we do not know" are the two answers a reader
  // must never see collapsed. `truncateToCents` returns null for both.
  if (cents === null) return `${symbolFor(currency)}—`;
  // Truncation has a second collapse in it, and `showBelowCent` is the way out.
  //
  // An amount above zero but below a cent truncates to `0.00` — so a task that cost
  // `0.004` renders exactly like one that cost nothing. Callers comparing spend against
  // a limit can live with that (understating by under a cent never invents a breach,
  // which is what the paragraph above is about). A caller whose whole job is to tell
  // "free" from "too small to show" cannot: for it, this is the same collapse that
  // 071 FR-012 and 089 exist to remove, arriving at the last step instead of the first.
  if (showBelowCent && Number(cents) === 0 && !isZero(amount)) {
    return `<${symbolFor(currency)}0.01`;
  }
  // Formatted through `toLocaleString` so the separators stay the reader's, but fed a
  // value already exact to two places — so this only renders, it never re-rounds.
  return `${symbolFor(currency)}${Number(cents).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Whether the amount is genuinely zero, at any scale.
 *
 * String surgery, like everything else here: `Number('0.0000')` is `0` and so is
 * `Number('0.004')` after truncation, which is exactly the confusion being resolved.
 * Every digit must be `0` for the answer to be yes.
 */
function isZero(amount: string): boolean {
  const match = PLAIN_DECIMAL.exec(amount.trim());
  if (match === null) return false;
  const [, , whole, fraction = ''] = match;
  return /^0*$/.test(whole) && /^0*$/.test(fraction);
}

/** A plain decimal, which is what the service's `NUMERIC` serialises to. */
const PLAIN_DECIMAL = /^([+-]?)(\d+)(?:\.(\d*))?$/;

/**
 * The amount with everything past the second decimal place dropped, as a string.
 *
 * String surgery rather than arithmetic: taking the first two characters of the
 * fractional part *is* truncation to cents, exactly, for any input the contract can
 * produce. Returns `null` for anything that is not a plain decimal — an empty field, a
 * scientific-notation value, a hex literal, or text — so the caller can say "unknown"
 * rather than invent a figure.
 */
function truncateToCents(amount: string): string | null {
  const match = PLAIN_DECIMAL.exec(amount.trim());
  if (match === null) return null;
  const [, sign, whole, fraction = ''] = match;
  // Truncation toward zero. Every amount the contract carries is non-negative, and for
  // a negative one this drops the remainder rather than growing the magnitude — which
  // is the reading of "never overstate" that survives a sign.
  return `${sign}${whole}.${fraction.padEnd(2, '0').slice(0, 2)}`;
}

/**
 * The same amount at full precision, for the one place a page shows it exactly.
 *
 * A reader who needs the real number needs *the* real number, so nothing here rounds and
 * no digit that carries information is dropped (spec 074 FR-002). What is dropped is the
 * padding: the service holds spend in `NUMERIC(24, 20)` and serialises it in full, so a
 * period costing twelve dollars arrives as `12.42088000000000000000` and rendered
 * verbatim it reads as a bug on the one line whose entire job is to be believed.
 *
 * **Never trimmed below two decimal places.** `replace(/0+$/, '')` is the obvious
 * implementation and it is wrong on most real amounts: it turns `10.10` into `10.1` —
 * dropping a place that *is* money — `10.00` into `10`, and `1000` into `1000`. Five of
 * the seven values in `specs/074-cost-surfaces-remediation/contracts/money-display.md`
 * come out wrong without the floor. Two places is the currency's minor unit, and there
 * is exactly one currency (`073` FR-032); a lookup table would imply a choice the
 * product does not offer.
 *
 * String surgery, like everything else in this module. Parsing to a number and calling
 * `toString()` would be the float path `formatMoney` spends a paragraph warning about,
 * arrived at from the other direction.
 */
export function exactMoney(amount: string, currency = 'USD'): string {
  const match = PLAIN_DECIMAL.exec(amount.trim());
  // Absence is not zero, on this form as much as on the rounded one. `$—` says "we do
  // not know"; a confident `$0.0000` beside a spend limit says something else entirely.
  if (match === null) return `${symbolFor(currency)}—`;
  const [, sign, whole, fraction = ''] = match;
  // Pad to the minor unit first, so `1000` and `10.1` reach two places; then trim back
  // to the last significant digit, and pad again to stop the trim going past it. The
  // second pad is the floor — without it this is the naive implementation above.
  const significant = fraction.padEnd(2, '0').replace(/0+$/, '');
  return `${symbolFor(currency)}${sign}${whole}.${significant.padEnd(2, '0')}`;
}

/**
 * Token counts, abbreviated. Tokens are supporting detail beneath cost (FR-018), so
 * they are always compact — there is no full-precision token figure to protect.
 */
export function formatTokens(tokens: number): string {
  if (tokens < 1_000) return String(tokens);
  if (tokens < 1_000_000) return `${trim(tokens / 1_000)}K`;
  if (tokens < 1_000_000_000) return `${trim(tokens / 1_000_000)}M`;
  return `${trim(tokens / 1_000_000_000)}B`;
}

/**
 * A proportion as whole percent, truncated for the same reason `formatMoney` truncates.
 *
 * The same IEEE-754 trap applies — `0.29 * 100` is `28.999999999999996`, so a bare
 * floor reads 29% as 28% — but the fix cannot be the same one. `consumed_ratio` arrives
 * as a JSON `number`, so unlike an amount there is no exact decimal string to truncate;
 * the value is already a float by the time it reaches here.
 *
 * So: absorb a nanopercent of representation error before flooring. That is far smaller
 * than any real remainder — a genuine 29.99% still reads 29% — while a value that is
 * only *arithmetically* a hair under a whole percent lands on it. The boundary FR-017
 * actually cares about, 100%, is unaffected either way: a ratio meaningfully below 1
 * cannot reach it, and one within a nanopercent of 1 has reached the limit.
 */
export function formatPercent(ratio: number): string {
  return `${String(Math.floor(ratio * 100 + 1e-9))}%`;
}

function trim(value: number): string {
  // One decimal place, and no trailing `.0` — `1.5M` and `2M`, never `2.0M`.
  return value.toFixed(1).replace(/\.0$/, '');
}

function symbolFor(currency: string): string {
  // Deliberately not a lookup table of every currency. There is exactly one
  // (`073` FR-032), and a table would imply otherwise; anything unrecognised falls
  // back to the code so the number is never shown bare (FR-016).
  return currency === 'USD' ? '$' : `${currency} `;
}

/**
 * The exact total of several amounts, or `null` if any of them cannot be read.
 *
 * `BigInt` over scaled integers, because this is money: `0.1 + 0.2` is
 * `0.30000000000000004` in IEEE-754, and the service serialises `NUMERIC` as a string
 * precisely so the value does not become a float on the way to a screen. Adding is the
 * last place that could undo it.
 *
 * **Full precision through the addition; cents only at display.** Truncating each
 * amount first — reusing `formatMoney`'s rule, which is right for one figure — loses
 * every sub-cent run: ten runs at `0.004` are `0.04`, but ten truncated to `0.00` are
 * nothing. LLM runs routinely cost fractions of a cent, so a board of them would read as
 * free. That is the same silent under-count 071 FR-012 exists to prevent, reached from
 * the other direction (spec 089 research R3).
 *
 * `null` rather than a partial sum when an amount is unreadable, so a caller must decide
 * what an unstatable figure means rather than being handed a total quietly missing one.
 */
export function sumMoney(amounts: readonly string[]): string | null {
  let scale = 0;
  const parsed: { digits: bigint; scale: number }[] = [];
  for (const amount of amounts) {
    const match = PLAIN_DECIMAL.exec(amount.trim());
    if (match === null) return null;
    const [, sign, whole, fraction = ''] = match;
    parsed.push({
      digits: BigInt(`${sign === '-' ? '-' : ''}${whole}${fraction}`),
      scale: fraction.length,
    });
    scale = Math.max(scale, fraction.length);
  }

  let total = 0n;
  for (const { digits, scale: own } of parsed) {
    total += digits * 10n ** BigInt(scale - own);
  }
  return unscale(total, scale);
}

/** A scaled integer back to a decimal string, with the point put back where it was. */
function unscale(total: bigint, scale: number): string {
  const negative = total < 0n;
  const digits = (negative ? -total : total)
    .toString()
    .padStart(scale + 1, '0');
  const whole = digits.slice(0, digits.length - scale);
  const fraction = digits.slice(digits.length - scale);
  return `${negative ? '-' : ''}${whole}${fraction ? `.${fraction}` : ''}`;
}
